/**
 * Outbox: the ordered queue of commands made on the phone.
 *
 * The decision logic (what to send next, how to apply results, backoff) is PURE — functions of the
 * records and `now` — so it is unit-tested without IndexedDB timing. The Dexie functions at the
 * bottom are thin wrappers.
 */
import { newId } from '@/lib/ids'
import {
  getDb,
  type DraftRecord,
  type OutboxRecord,
  type OutboxStatus,
  type SetAsideInfo,
  type SupplyChainDB,
} from './db'
import {
  MAX_COMMANDS_PER_BATCH,
  TERMINAL_ERROR_RESULTS,
  TERMINAL_OK_RESULTS,
  type CaptureMode,
  type CommandResult,
  type CommandType,
} from './protocol'
import { validatePayload } from './commandRegistry'
import { openOutboxRecords, sealOutboxRecord, sealValue } from './sealed'

// ---------------------------------------------------------------------------------------------
// Pure policy
// ---------------------------------------------------------------------------------------------

/** Statuses meaning "the server has it" — nothing more to send. */
export const DONE_STATUSES: ReadonlySet<OutboxStatus> = new Set(['APPLIED', 'DUPLICATE', 'HELD'])
/** Statuses the engine may (re)send once due. */
export const SENDABLE_STATUSES: ReadonlySet<OutboxStatus> = new Set(['PENDING', 'WAIT_FOR_PARENT'])

export const BACKOFF_BASE_MS = 2_000
export const BACKOFF_MAX_MS = 5 * 60_000
/**
 * WAIT_FOR_PARENT tries before a person is asked to look (about a day at the 5-min cap). Its
 * parent was refused (never stored on the server) or is on a phone that has stopped syncing;
 * waiting quietly for ever would leave the work invisible.
 */
export const MAX_WAIT_FOR_PARENT_ATTEMPTS = 300

/**
 * Exponential backoff with ±20% jitter: 2s, 4s, 8s … capped at 5 min.
 * `attempts` is the number of failed attempts so far (≥1). `random` is injectable for tests.
 */
export function backoffDelay(attempts: number, random: () => number = Math.random): number {
  const exp = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.max(0, attempts - 1))
  const jitter = 1 + (random() * 0.4 - 0.2)
  return Math.round(Math.min(BACKOFF_MAX_MS, exp * jitter))
}

export function sortBySeq<T extends Pick<OutboxRecord, 'seq'>>(records: readonly T[]): T[] {
  return [...records].sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))
}

/**
 * Pick the next batch, oldest first.
 *  - only PENDING / WAIT_FOR_PARENT records that are due (`next_attempt_at <= now`)
 *  - a record whose parent is still local and not done is sent only if the parent is earlier in
 *    the same batch. A parent the server refused (ERROR) does NOT hold its children back: they are
 *    sent anyway and the server answers WAIT_FOR_PARENT for any that truly need it (nothing is
 *    stored, it retries). One refused record never stops the rest of the day.
 *  - a parent id we do not know (made on another phone, or pruned) is assumed to be on the server
 */
export function selectBatch(
  records: readonly OutboxRecord[],
  now: number,
  max: number = MAX_COMMANDS_PER_BATCH,
): OutboxRecord[] {
  const byId = new Map(records.map((r) => [r.id, r]))
  const batch: OutboxRecord[] = []
  const inBatch = new Set<string>()

  for (const rec of sortBySeq(records)) {
    if (batch.length >= max) break
    if (!SENDABLE_STATUSES.has(rec.status) || rec.next_attempt_at > now) continue
    const parentsReady = rec.parent_ids.every((pid) => {
      const parent = byId.get(pid)
      return (
        !parent || DONE_STATUSES.has(parent.status) || parent.status === 'ERROR' || inBatch.has(pid)
      )
    })
    if (!parentsReady) continue
    batch.push(rec)
    inBatch.add(rec.id)
  }
  return batch
}

/** Statuses that hold back everything that depends on them until a person acts. */
const BLOCKING_STATUSES: ReadonlySet<OutboxStatus> = new Set(['SET_ASIDE'])

/** Ids of records that cannot be sent because an ancestor is in ERROR (or was set aside). */
export function blockedByError(records: readonly OutboxRecord[]): Set<string> {
  const byId = new Map(records.map((r) => [r.id, r]))
  const memo = new Map<string, boolean>()
  const blocked = (id: string, seen: Set<string>): boolean => {
    if (memo.has(id)) return memo.get(id)!
    const rec = byId.get(id)
    if (!rec || seen.has(id)) return false
    seen.add(id)
    const v = rec.parent_ids.some((pid) => {
      const parent = byId.get(pid)
      return (parent !== undefined && BLOCKING_STATUSES.has(parent.status)) || blocked(pid, seen)
    })
    memo.set(id, v)
    return v
  }
  const out = new Set<string>()
  for (const r of records)
    if (SENDABLE_STATUSES.has(r.status) && blocked(r.id, new Set())) out.add(r.id)
  return out
}

export type OutboxPatch = Partial<
  Pick<
    OutboxRecord,
    | 'status'
    | 'attempts'
    | 'next_attempt_at'
    | 'last_error'
    | 'last_error_code'
    | 'result'
    | 'acked_at'
  >
>

/** Turn per-command server results into record updates. Commands missing from the reply retry. */
export function planResultUpdates(
  sent: readonly OutboxRecord[],
  results: readonly CommandResult[],
  now: number,
  random: () => number = Math.random,
): Map<string, OutboxPatch> {
  const byId = new Map(results.map((r) => [r.id, r]))
  const updates = new Map<string, OutboxPatch>()

  for (const rec of sent) {
    const res = byId.get(rec.id)
    if (!res) {
      const attempts = rec.attempts + 1
      updates.set(rec.id, {
        status: 'PENDING',
        attempts,
        next_attempt_at: now + backoffDelay(attempts, random),
        last_error: 'No result for this command in the server reply',
        last_error_code: 'NO_RESULT',
      })
      continue
    }
    if (TERMINAL_OK_RESULTS.has(res.result)) {
      updates.set(rec.id, {
        status: res.result as OutboxStatus,
        result: res,
        acked_at: now,
        last_error: null,
        last_error_code: null,
      })
    } else if (res.result === 'WAIT_FOR_PARENT') {
      const attempts = rec.attempts + 1
      if (attempts >= MAX_WAIT_FOR_PARENT_ATTEMPTS) {
        updates.set(rec.id, {
          status: 'ERROR',
          result: res,
          attempts,
          last_error:
            'Waited a day for data from another phone or an earlier entry. Make sure every phone of this team syncs; if it still waits, a supervisor can set it aside.',
          last_error_code: 'PARENT_NEVER_ARRIVED',
        })
        continue
      }
      updates.set(rec.id, {
        status: 'WAIT_FOR_PARENT',
        result: res,
        attempts,
        next_attempt_at: now + backoffDelay(attempts, random),
        last_error: 'Waiting for an earlier command to reach the server',
        last_error_code: 'WAIT_FOR_PARENT',
      })
    } else if (res.result === 'ERROR') {
      // The server failed and rolled back: keep it and try again later.
      const attempts = rec.attempts + 1
      updates.set(rec.id, {
        status: 'PENDING',
        result: res,
        attempts,
        next_attempt_at: now + backoffDelay(attempts, random),
        last_error: res.error?.message ?? 'The server could not apply it yet; it will try again',
        last_error_code: res.error?.code ?? 'SERVER_ERROR',
      })
    } else if (TERMINAL_ERROR_RESULTS.has(res.result)) {
      updates.set(rec.id, {
        status: 'ERROR',
        result: res,
        attempts: rec.attempts + 1,
        // When it is tried again by itself (autoRetryRefused): a server fix often makes it go.
        next_attempt_at: now + refusedRetryDelay(rec.attempts + 1),
        last_error:
          res.error?.message ??
          (res.result === 'CONFLICT'
            ? 'Same id sent with different data'
            : 'Server rejected the command as malformed'),
        last_error_code: res.result,
      })
    }
  }
  return updates
}

/** All sent records go back to PENDING with backoff after a transient transport failure. */
export function planTransientFailure(
  sent: readonly OutboxRecord[],
  message: string,
  code: string,
  now: number,
  random: () => number = Math.random,
): Map<string, OutboxPatch> {
  const updates = new Map<string, OutboxPatch>()
  for (const rec of sent) {
    const attempts = rec.attempts + 1
    updates.set(rec.id, {
      status: rec.status === 'SENDING' || rec.status === 'PENDING' ? 'PENDING' : rec.status,
      attempts,
      next_attempt_at: now + backoffDelay(attempts, random),
      last_error: message,
      last_error_code: code,
    })
  }
  return updates
}

// ---------------------------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------------------------

export interface EnqueueInput<P = unknown> {
  type: CommandType
  payload: P
  actorId: string
  sessionId?: string | null
  captureMode?: CaptureMode
  /** Earlier command ids this one depends on (e.g. commit_delivery → open_session). */
  parentIds?: string[]
  /** Photo ids (from PhotoCapture) referenced by the payload. */
  mediaIds?: string[]
  /** Provide when the id is also used inside the payload; otherwise one is made. */
  id?: string
  occurredAt?: Date
}

type Listener = () => void
const enqueueListeners = new Set<Listener>()
/** The sync engine listens to start a drain shortly after new work arrives. */
export function onEnqueue(l: Listener): () => void {
  enqueueListeners.add(l)
  return () => enqueueListeners.delete(l)
}

/** Build a validated, unsent record. Throws InvalidCommandPayloadError on a bad payload. */
export function buildOutboxRecord<P>(
  input: EnqueueInput<P>,
  now: number = Date.now(),
): OutboxRecord {
  return {
    id: input.id ?? newId(),
    type: input.type,
    actor_id: input.actorId,
    occurred_at: (input.occurredAt ?? new Date(now)).toISOString(),
    session_id: input.sessionId ?? null,
    capture_mode: input.captureMode ?? 'APP',
    payload: validatePayload(input.type, input.payload),
    parent_ids: input.parentIds ?? [],
    media_ids: input.mediaIds ?? [],
    created_at: now,
    status: 'PENDING',
    attempts: 0,
    next_attempt_at: 0,
    last_error: null,
    last_error_code: null,
    result: null,
    acked_at: null,
  }
}

/** Validate and append a command. Resolves once it is durably in IndexedDB. */
export async function enqueueCommand<P>(
  input: EnqueueInput<P>,
  db: SupplyChainDB = getDb(),
): Promise<OutboxRecord> {
  const rec = buildOutboxRecord(input)
  const seq = await db.outbox.add(await sealOutboxRecord(rec, db))
  enqueueListeners.forEach((l) => l())
  return { ...rec, seq }
}

/**
 * Append a command and drop the draft it was made from, in one IndexedDB transaction: a crash
 * can never leave both the command and the draft (which could then be committed or cancelled a
 * second time) or neither.
 */
export async function enqueueFromDraft<P>(
  input: EnqueueInput<P>,
  draftId: string,
  db: SupplyChainDB = getDb(),
): Promise<OutboxRecord> {
  const rec = buildOutboxRecord(input)
  const stored = await sealOutboxRecord(rec, db) // sealed before the transaction (WebCrypto)
  let seq: number | undefined
  await db.transaction('rw', db.outbox, db.drafts, async () => {
    seq = await db.outbox.add(stored)
    await db.drafts.delete(draftId)
  })
  enqueueListeners.forEach((l) => l())
  return { ...rec, seq }
}

/**
 * Append a command and save a draft that must move with it (e.g. the line's counter reading at a
 * pack close), in one IndexedDB transaction: never the command without the draft or the reverse.
 */
export async function enqueueWithDraft<P, T>(
  input: EnqueueInput<P>,
  draft: Omit<DraftRecord<T>, 'created_at' | 'updated_at'>,
  db: SupplyChainDB = getDb(),
): Promise<OutboxRecord> {
  const rec = buildOutboxRecord(input)
  // Sealed before the transaction (WebCrypto).
  const stored = await sealOutboxRecord(rec, db)
  const data = await sealValue(draft.data, db)
  let seq: number | undefined
  await db.transaction('rw', db.outbox, db.drafts, async () => {
    seq = await db.outbox.add(stored)
    const existing = await db.drafts.get(draft.id)
    const now = Date.now()
    await db.drafts.put({
      ...draft,
      data,
      created_at: existing?.created_at ?? now,
      updated_at: now,
    } as DraftRecord)
  })
  enqueueListeners.forEach((l) => l())
  return { ...rec, seq }
}

/**
 * Enqueue several commands atomically, in order (e.g. load_bags then dispatch_shipment). Unless a
 * command names its own parents, each one depends on the one before it.
 */
export async function enqueueCommands(
  inputs: EnqueueInput[],
  db: SupplyChainDB = getDb(),
): Promise<OutboxRecord[]> {
  // Validate everything before writing anything.
  const records: OutboxRecord[] = []
  for (const input of inputs) {
    const prev = records.at(-1)
    records.push(
      buildOutboxRecord({ ...input, parentIds: input.parentIds ?? (prev ? [prev.id] : []) }),
    )
  }
  // Sealed before the transaction: WebCrypto promises would end it.
  const stored = await Promise.all(records.map((rec) => sealOutboxRecord(rec, db)))
  const out: OutboxRecord[] = []
  await db.transaction('rw', db.outbox, async () => {
    for (const [i, rec] of records.entries())
      out.push({ ...rec, seq: await db.outbox.add(stored[i]!) })
  })
  enqueueListeners.forEach((l) => l())
  return out
}

/** Apply planned patches in one transaction. */
export async function applyPatches(
  updates: Map<string, OutboxPatch>,
  db: SupplyChainDB = getDb(),
): Promise<void> {
  if (updates.size === 0) return
  await db.transaction('rw', db.outbox, async () => {
    for (const [id, patch] of updates) {
      await db.outbox.where('id').equals(id).modify(patch)
    }
  })
}

/**
 * Records still in the queue (not done), payloads opened. The queue is small (a day's work), so
 * load and decide in memory. A payload no key on this phone opens can never be sent: it is marked
 * ERROR (PAYLOAD_LOCKED) so a person sees it and its children stay blocked behind it.
 * SET_ASIDE records are included (never sent: not SENDABLE) so a command made later on top of one
 * is held back instead of being sent without its parent.
 */
export async function loadOpenRecords(db: SupplyChainDB = getDb()): Promise<OutboxRecord[]> {
  const stored = await db.outbox
    .where('status')
    .anyOf(['PENDING', 'SENDING', 'WAIT_FOR_PARENT', 'ERROR', 'SET_ASIDE'])
    .sortBy('seq')
  const opened = await openOutboxRecords(stored, db)
  const locked = opened.filter(
    (r) => r.payload_locked && r.status !== 'ERROR' && r.status !== 'SET_ASIDE',
  )
  if (locked.length > 0) {
    const patch: OutboxPatch = {
      status: 'ERROR',
      last_error: 'This command is encrypted on the phone and the key to open it is gone',
      last_error_code: 'PAYLOAD_LOCKED',
    }
    await applyPatches(new Map(locked.map((r) => [r.id, patch])), db)
    for (const r of locked) Object.assign(r, patch)
  }
  return opened.map(({ payload_locked: _l, ...r }) => r)
}

/**
 * Marks the batch SENDING and returns the ids it marked: only still-sendable rows, so one set
 * aside between the engine's load and here stays aside and is not sent.
 */
export async function markSending(ids: string[], db: SupplyChainDB = getDb()): Promise<string[]> {
  const marked: string[] = []
  await db.outbox
    .where('id')
    .anyOf(ids)
    .and((r) => SENDABLE_STATUSES.has(r.status))
    .modify((r) => {
      r.status = 'SENDING'
      marked.push(r.id)
    })
  return marked
}

/** After a crash mid-request: SENDING → PENDING. The server is idempotent, so resending is safe. */
export async function recoverInFlight(db: SupplyChainDB = getDb()): Promise<number> {
  return db.outbox.where('status').equals('SENDING').modify({ status: 'PENDING' })
}

/** A refused record is tried again by itself after 30 min, then longer, at most every 6 h. */
export const REFUSED_RETRY_BASE_MS = 30 * 60_000
export const REFUSED_RETRY_MAX_MS = 6 * 60 * 60_000
export function refusedRetryDelay(attempts: number): number {
  return Math.min(REFUSED_RETRY_MAX_MS, REFUSED_RETRY_BASE_MS * Math.max(1, attempts))
}

/**
 * Records the server refused as malformed go back in the queue by themselves when their wait is
 * over — a refusal is often fixed on the server (as on 25 Oct), and the records behind it should
 * not wait for someone to notice and tap Retry. Resending is safe: the server is idempotent. A
 * CONFLICT (same id, different data) never fixes itself and stays for a person.
 */
export async function autoRetryRefused(
  now = Date.now(),
  db: SupplyChainDB = getDb(),
): Promise<number> {
  const n = await db.outbox
    .where('status')
    .equals('ERROR')
    .filter((r) => r.result?.result === 'REJECTED_MALFORMED' && r.next_attempt_at <= now)
    .modify({ status: 'PENDING' })
  if (n) enqueueListeners.forEach((l) => l())
  return n
}

/** A person decided to retry an ERROR command (e.g. after the office fixed data). */
export async function retryCommand(id: string, db: SupplyChainDB = getDb()): Promise<void> {
  await db.outbox
    .where('id')
    .equals(id)
    .modify({ status: 'PENDING', attempts: 0, next_attempt_at: 0 })
  enqueueListeners.forEach((l) => l())
}

/** Records that may be set aside: still in the queue, not in flight. */
const SET_ASIDE_FROM: ReadonlySet<OutboxStatus> = new Set(['PENDING', 'WAIT_FOR_PARENT', 'ERROR'])

/**
 * What setting `rootId` aside takes with it (pure): the record itself plus every record that
 * depends on it, directly or through others, that is not yet on the server. The root must be in
 * ERROR, or be held back by an ancestor in ERROR / set aside (it can never be sent as it is).
 * Returns [] when nothing may be set aside.
 */
export function setAsideTargets(records: readonly OutboxRecord[], rootId: string): OutboxRecord[] {
  const root = records.find((r) => r.id === rootId)
  if (!root || !SET_ASIDE_FROM.has(root.status)) return []
  if (root.status !== 'ERROR' && !blockedByError(records).has(rootId)) return []
  const children = new Map<string, OutboxRecord[]>()
  for (const r of records)
    for (const pid of r.parent_ids) children.set(pid, [...(children.get(pid) ?? []), r])
  const out: OutboxRecord[] = []
  const seen = new Set<string>()
  const walk = (rec: OutboxRecord) => {
    if (seen.has(rec.id)) return
    seen.add(rec.id)
    if (!SET_ASIDE_FROM.has(rec.status)) return
    out.push(rec)
    for (const c of children.get(rec.id) ?? []) walk(c)
  }
  walk(root)
  return sortBySeq(out)
}

/**
 * A supervisor confirmed (PIN) that this command cannot go through as it is: it and everything
 * that depends on it leave the active queue as SET_ASIDE, so the rest of the outbox flows. Never
 * deleted: they stay on the Sync screen (with who, when and why) and can be put back. Resolves
 * with the records set aside.
 */
export async function setAsideCommand(
  rootId: string,
  by: { userId: string | null; name: string | null; reason: string },
  db: SupplyChainDB = getDb(),
  now: number = Date.now(),
): Promise<string[]> {
  let ids: string[] = []
  await db.transaction('rw', db.outbox, async () => {
    const records = await db.outbox
      .where('status')
      .anyOf([...SET_ASIDE_FROM, 'SET_ASIDE'])
      .toArray()
    const targets = setAsideTargets(records, rootId)
    ids = targets.map((r) => r.id)
    for (const rec of targets) {
      const set_aside: SetAsideInfo = {
        at: now,
        by_user_id: by.userId,
        by_name: by.name,
        reason: by.reason,
        root_id: rootId,
        previous_status: rec.status,
      }
      await db.outbox.where('id').equals(rec.id).modify({ status: 'SET_ASIDE', set_aside })
    }
  })
  return ids
}

/** Undo a set-aside: the root and what was set aside with it go back to the queue and are sent again. */
export async function putBackCommand(rootId: string, db: SupplyChainDB = getDb()): Promise<number> {
  const n = await db.outbox
    .where('status')
    .equals('SET_ASIDE')
    .and((r) => r.set_aside?.root_id === rootId)
    .modify((r) => {
      r.status = 'PENDING'
      r.attempts = 0
      r.next_attempt_at = 0
      delete r.set_aside
    })
  enqueueListeners.forEach((l) => l())
  return n
}

/** Delete finished commands older than `olderThanMs` (default 30 days) to bound storage. */
export async function pruneDone(
  olderThanMs = 30 * 86_400_000,
  now = Date.now(),
  db: SupplyChainDB = getDb(),
): Promise<number> {
  const cutoff = now - olderThanMs
  return db.outbox
    .where('status')
    .anyOf([...DONE_STATUSES])
    .and((r) => (r.acked_at ?? r.created_at) < cutoff)
    .delete()
}

export interface OutboxCounts {
  pending: number
  held: number
  errors: number
  /** Taken out of the queue by a supervisor; kept on the phone. */
  setAside: number
  total: number
}

export async function countOutbox(db: SupplyChainDB = getDb()): Promise<OutboxCounts> {
  const [pending, held, errors, setAside, total] = await Promise.all([
    db.outbox.where('status').anyOf(['PENDING', 'SENDING', 'WAIT_FOR_PARENT']).count(),
    db.outbox.where('status').equals('HELD').count(),
    db.outbox.where('status').equals('ERROR').count(),
    db.outbox.where('status').equals('SET_ASIDE').count(),
    db.outbox.count(),
  ])
  return { pending, held, errors, setAside, total }
}
